import { IMG } from "@/lib/site";
import Reveal from "@/components/site/Reveal";
import SectionCTA from "@/components/site/SectionCTA";
import { Wind, Leaf, Heart, Sparkles, Hand, Moon, Sun } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { useT } from "@/lib/i18n";

const MetodoTao = () => {
  const t = useT();
  useSeo({
    title: {
      es: "Qué es el Método TAO · Acompañamiento Cuerpo-Mente Cáncer y Fibromialgia",
      ca: "Què és el Mètode TAO · Acompanyament Cos-Ment Càncer i Fibromiàlgia",
    },
    description: {
      es: "Método TAO es un sistema de acompañamiento cuerpo-mente para aliviar la fatiga crónica, la fibromialgia y las secuelas del cáncer. Combina yoga restaurativo, respiración terapéutica, mindfulness y regulación del sistema nervioso. Creado por Marta Montardit.",
      ca: "El Mètode TAO és un sistema d'acompanyament cos-ment per alleujar la fatiga crònica, la fibromiàlgia i les seqüeles del càncer. Combina ioga restauratiu, respiració terapèutica, mindfulness i regulació del sistema nerviós. Creat per la Marta Montardit.",
    },
    path: "/metodo-tao",
  });

  const pillars = [
    { icon: Wind, title: t("method.pillar1.t"), body: t("method.pillar1.b") },
    { icon: Leaf, title: t("method.pillar2.t"), body: t("method.pillar2.b") },
    { icon: Hand, title: t("method.pillar3.t"), body: t("method.pillar3.b") },
    { icon: Sparkles, title: t("method.pillar4.t"), body: t("method.pillar4.b") },
    { icon: Heart, title: t("method.pillar5.t"), body: t("method.pillar5.b") },
    { icon: Sun, title: t("method.pillar6.t"), body: t("method.pillar6.b") },
    { icon: Moon, title: t("method.pillar7.t"), body: t("method.pillar7.b") },
  ];

  return (
  <div className="bg-ivory">
    <section className="pt-12 md:pt-20 pb-16 md:pb-24" data-testid="page-metodo-tao">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <Reveal className="lg:col-span-7">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">{t("method.eyebrow")}</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal leading-[0.98]">
            {t("method.title1")} <span className="italic text-terracotta">{t("method.title2")}</span>.
          </h1>
          <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-xl">
            {t("method.lead")}
          </p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="aspect-[4/5] rounded-tr-[4rem] rounded-bl-[4rem] overflow-hidden bg-sand">
            <img src={IMG.restorativeChair} alt="Método TAO - práctica restaurativa" className="w-full h-full object-cover" fetchPriority="high" decoding="async" />
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-20 md:py-28 bg-sand/30 grain">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">{t("method.pillarsEyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
            {t("method.pillarsTitle1")} <span className="italic">{t("method.pillarsTitle2")}</span>.
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.05}>
              <div className="bg-ivory rounded-3xl p-8 border border-sand/60 h-full hover:-translate-y-1 hover:shadow-lg hover:shadow-charcoal/5 transition-all">
                <span className="w-12 h-12 rounded-full bg-sand/70 flex items-center justify-center text-olive">
                  <p.icon size={20} />
                </span>
                <h3 className="mt-6 font-serif text-2xl text-charcoal">{p.title}</h3>
                <p className="mt-3 text-charcoal/65 text-[15px] leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 md:py-28">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <Reveal className="lg:col-span-6 order-2 lg:order-1">
          <div className="aspect-[5/6] rounded-3xl overflow-hidden">
            <img src={IMG.yogaBuddha} alt="Clase grupal Método TAO" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6 order-1 lg:order-2">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">{t("method.howEyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
            {t("method.howTitle1")} <span className="italic">{t("method.howTitle2")}</span>{t("method.howTitle3")}
          </h2>
          <ul className="mt-8 space-y-5 text-charcoal/80 text-[16px] leading-relaxed">
            <li>· {t("method.how.1")}</li>
            <li>· {t("method.how.2")}</li>
            <li>· {t("method.how.3")}</li>
            <li>· {t("method.how.4")}</li>
            <li>· {t("method.how.5")}</li>
          </ul>
        </Reveal>
      </div>
    </section>

    <SectionCTA title={t("method.ctaTitle")} body={t("method.ctaBody")} />
  </div>
  );
};

export default MetodoTao;
